import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Button, Table, Badge } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdEdit, MdDelete, MdSettings, MdQuestionAnswer } from 'react-icons/md';

// components
import Page from '../components/Page';
// My components
import ReactDataTable from '../mycomponents/ReactDataTable';
// lib
import { dateTimeFormat } from '../lib/date';
import toastAlert from '../lib/toastAlert';
// api
import { getBlog, updateBlogStatus, deleteBlog, updateIsMain } from '../api/admin';


const BlogList = () => {

    const navigate = useNavigate();

    const columns = [
        {
            key: "title",
            text: "Title",
            className: "title",
            align: "left",
            sortable: false,
            cell: ({ title }) => {
                if (title.length > 100) {
                    return `${title?.substring(0, 100)}...`
                } else return title;
            }
        },
        {
            key: "category",
            text: "Category",
            className: "category",
            align: "left",
            sortable: false
        },
        {
            key: "status",
            text: "Status",
            className: "status",
            align: "left",
            sortable: false,
            cell: ({ _id, status, isMain }) => {
                return (
                    <Badge
                        color={status === 'Published' ? "success" : "danger"}
                        style={{ fontSize: "12px", padding: "10px 16px", borderRadius: "10px", cursor: "pointer" }}
                        onClick={() => handleStatusChange({ _id, status }, isMain)}
                    >
                        {status}
                    </Badge>
                )
            }
        },
        {
            key: "isMain",
            text: "Is Main",
            className: "isMain",
            align: "left",
            sortable: false,
            cell: ({ _id, isMain }) => {
                return (
                    <div
                        onClick={() => {
                            if (!isMain) {
                                handleIsMainToggle({ _id })
                            }
                        }}
                        disabled={isMain}
                        style={{
                            width: "50px",
                            height: "25px",
                            borderRadius: "20px",
                            background: isMain ? "#28a745" : "#ccc",
                            display: "flex",
                            alignItems: "center",
                            padding: "3px",
                            cursor: "pointer",
                            transition: "0.3s"
                        }}
                    >
                        <div
                            style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                background: "#fff",
                                transform: isMain ? "translateX(25px)" : "translateX(0px)",
                                transition: "0.3s"
                            }}
                        />
                    </div>
                )
            }
        },
        {
            key: "createdAt",
            text: "Created At",
            className: "createdAt",
            align: "left",
            sortable: false,
            cell: ({ createdAt }) => {
                return dateTimeFormat(createdAt)
            }
        },
        {
            key: "publishedAt",
            text: "Published At",
            className: "publishedAt",
            align: "left",
            sortable: false,
            cell: ({ publishedAt }) => {
                if (publishedAt) return dateTimeFormat(publishedAt)
                else return '-'
            }
        },
        {
            key: "action",
            text: "Action",
            className: "action",
            align: "left",
            sortable: false,
            cell: state => {
                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                            color="primary"
                            title="Faq"
                            onClick={
                                () => navigate(`/blog-faq-list/${state?._id}`)
                            }
                        >
                            <MdQuestionAnswer />
                        </Button>
                        <Button
                            color="primary"
                            title="Seo Settings"
                            onClick={
                                () => navigate(`/seo-edit/${state._id}`)
                            }
                        >
                            <MdSettings />
                        </Button>
                        <Button
                            color="secondary"
                            title="Edit"
                            onClick={
                                () => navigate('/blog-edit', { state })
                            }
                        >
                            <MdEdit />
                        </Button>
                        <Button
                            color="danger"
                            title="Delete"
                            onClick={
                                () => handleDelete({ _id: state?._id }, state?.isMain)
                            }
                        >
                            <MdDelete />
                        </Button>
                    </div>
                )
            }
        }
    ];


    // state
    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [loader, setLoader] = useState(false);
    const [pagination, setPagination] = useState({ page: "", limit: "", search: "" });
    // const { page, limit, search } = pagination;

    const handleStatusChange = async (data, isMain) => {
        if (data?.status == 'Published' && isMain == true) {
            return toastAlert('error', 'This is a primary blog; unpublishing it is not an option.', 'updateBlogStatus')
        }
        try {
            let { status, message } = await updateBlogStatus(data);
            if (status === true) {
                toastAlert('success', message, 'updateBlogStatus')
                fetchData();
            }
        } catch (error) { }
    }

    const handleIsMainToggle = async ({ _id }) => {
        try {
            let { status, message } = await updateIsMain({ _id });
            if (status === true) {
                toastAlert('success', message, 'updateIsMain')
                fetchData();
            }
        } catch (error) { }
    }

    const handleDelete = async (data, isMain) => {
        try {
            if (isMain == true) {
                return toastAlert('error', 'This is a primary blog; deleting it is not an option.', 'handleDelete')
            }

            if (window.confirm('Are you sure want to delete this blog?')) {
                let { status, message } = await deleteBlog(data);
                if (status === true) {
                    toastAlert('success', message, 'updateBlogStatus')
                    fetchData();
                }
            }
        } catch (error) { }
    }

    const fetchData = useCallback(async () => {
        try {
            setLoader(true);
            const { status, result } = await getBlog(pagination);
            setLoader(false);
            if (status === true) {
                setData(result.data)
                setCount(result.count)
            }
        } catch (err) { }
    }, [pagination])

    useEffect(() => {
        fetchData()
    }, [pagination, fetchData])

    return (
        <Page
            title="Blog List"
            breadcrumbs={[{ name: 'Blog List', active: true }]}
            className="TablePage"
        >
            <Row>
                <Col>
                    <Card className="mb-3">
                        <CardHeader className="d-flex align-items-center justify-content-between">
                            <span>Blog List</span>
                            <Button color="primary" onClick={() => navigate('/blog-add')}><MdAdd />Add</Button>
                        </CardHeader>
                        <CardBody>
                            <Table responsive={false}>
                                <ReactDataTable
                                    columns={columns}
                                    records={data}
                                    count={count}
                                    loader={loader}
                                    setPagination={setPagination}
                                />
                            </Table>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Page>
    );
};

export default BlogList;